/**
 * usePendingApprovals Hook
 * AIDLC Administrative Backend
 *
 * One source for the approval cards on the Agent Dashboard and in Chat:
 *   - loads the waiting approvals through the authenticated apiClient (APPROVAL-TOKEN-1, BL-beb6a8cc)
 *   - follows 'approval:request' / 'approval:response' socket pushes
 *   - removes a card only after the server accepted the answer; a refused or failed answer keeps the
 *     card and reports a plain message for it
 *   - reports a failed load instead of hiding it (MAYA-AW-02, BL-86788e85), with a retry (PORTAL-RELOAD-1)
 *   - tracks answers in flight per card, so one card's answer returning never re-enables another card's
 *     buttons (BUG-AT-01, BL-cc845c93)
 *
 * Only Admins and Operators may answer (the server's APPROVER_ROLES). For anyone else nothing is asked
 * for, so a Viewer never sees an error for a list they are not meant to have.
 */

import { useState, useEffect, useCallback } from 'react';
import { useBridge } from '../components/layout/MainLayout';
import { useAuth } from './useAuth';
import {
  getPendingApprovals,
  respondToApproval,
  approvalAnswerError,
  APPROVALS_LOAD_ERROR,
  type ApprovalRequest,
  type ApprovalDecision,
} from '../services/approvalService';

interface UsePendingApprovalsResult {
  approvals: ApprovalRequest[];
  /** Plain message when the list could not be loaded, else null. */
  loadError: string | null;
  /** True while the list is being loaded (first load or a retry). */
  loading: boolean;
  /** Loads the list again (the Try again button under a load error). */
  retryLoad: () => void;
  /** True while an answer for this request is on its way. Each card has its own. */
  isResponding: (requestId: string) => boolean;
  /** Plain message per request id whose last answer was not accepted. */
  answerErrors: Record<string, string>;
  /** Sends an answer; resolves true only when the server accepted it. */
  respond: (requestId: string, decision: ApprovalDecision, message: string) => Promise<boolean>;
}

function withoutKey(map: Record<string, string>, key: string): Record<string, string> {
  if (!(key in map)) return map;
  const next = { ...map };
  delete next[key];
  return next;
}

function withId(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (set.has(id)) return set;
  const next = new Set(set);
  next.add(id);
  return next;
}

function withoutId(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (!set.has(id)) return set;
  const next = new Set(set);
  next.delete(id);
  return next;
}

export function usePendingApprovals(): UsePendingApprovalsResult {
  const { socket } = useBridge();
  const { isAdmin, isOperator } = useAuth();
  const canAnswer = isAdmin || isOperator;
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /** Bumped by retryLoad; the load effect runs again for each new value. */
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [responding, setResponding] = useState<ReadonlySet<string>>(() => new Set());
  const [answerErrors, setAnswerErrors] = useState<Record<string, string>>({});

  const removeApproval = useCallback((requestId: string) => {
    setApprovals((prev) => prev.filter((a) => a.id !== requestId));
    setAnswerErrors((prev) => withoutKey(prev, requestId));
  }, []);

  // The list (independent of the socket): on first use and on each retry
  useEffect(() => {
    if (!canAnswer) return;
    let cancelled = false;
    setLoading(true);
    getPendingApprovals()
      .then((list) => {
        if (cancelled) return;
        setLoadError(null);
        // Merge: a socket push may have arrived before the list did
        setApprovals((prev) => [...list, ...prev.filter((p) => !list.some((a) => a.id === p.id))]);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[usePendingApprovals] load failed', err);
        setLoadError(APPROVALS_LOAD_ERROR);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [canAnswer, loadAttempt]);

  const retryLoad = useCallback(() => setLoadAttempt((n) => n + 1), []);

  // Live pushes: a new request, or an answer accepted from any tab or path
  useEffect(() => {
    if (!socket) return;
    const handleRequest = (data: ApprovalRequest) => {
      setApprovals((prev) => (prev.some((a) => a.id === data.id) ? prev : [...prev, data]));
    };
    const handleResponse = (data: { request?: { id?: string } }) => {
      const id = data?.request?.id;
      if (id) removeApproval(id);
    };
    socket.on('approval:request', handleRequest);
    socket.on('approval:response', handleResponse);
    return () => {
      socket.off('approval:request', handleRequest);
      socket.off('approval:response', handleResponse);
    };
  }, [socket, removeApproval]);

  const respond = useCallback(async (requestId: string, decision: ApprovalDecision, message: string) => {
    setResponding((prev) => withId(prev, requestId));
    setAnswerErrors((prev) => withoutKey(prev, requestId));
    try {
      await respondToApproval(requestId, decision, message);
      removeApproval(requestId);
      return true;
    } catch (err) {
      console.error('[usePendingApprovals] answer not accepted', err);
      setAnswerErrors((prev) => ({ ...prev, [requestId]: approvalAnswerError(err) }));
      return false;
    } finally {
      setResponding((prev) => withoutId(prev, requestId));
    }
  }, [removeApproval]);

  const isResponding = useCallback((requestId: string) => responding.has(requestId), [responding]);

  return { approvals, loadError, loading, retryLoad, isResponding, answerErrors, respond };
}
